// Counter Intelligence — spreadsheet (Excel) upload helpers.
//
// The chat accepts the same files as the import pages (.csv,.xlsx,.xls,…), but
// an .xlsx/.xls is a binary zip — reading it with file.text() hands the AI
// garbage and every Excel upload fails with "I couldn't read that file."
// The import pages solved this long ago: parse the workbook with SheetJS and
// send the rows as CSV text (see items-add-experience.js parseExcelItemsBySheet
// and UpdatePrices.jsx). These are the pure pieces of that same conversion so
// the drawer's item import AND price update read Excel exactly like the pages
// do, on every page the chat is open on.
//
// Pure (no React / no DOM / no network) so it is unit-testable. The drawer owns
// the I/O: loading SheetJS on demand and reading the File into an ArrayBuffer.

// Same build the import pages ship (add-items.html / items.html / invoice-inbox.html),
// loaded on demand on pages that don't include it.
export const SHEETJS_SRC = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';

// Is this upload a spreadsheet that needs SheetJS, rather than plain text?
// Extension first: Windows tags real CSVs with an Excel MIME
// (application/vnd.ms-excel), and those read fine as text — routing them
// through SheetJS would be pointless. Everything the OS calls a sheet/Excel
// document without a .csv/.txt name gets parsed.
export function isSpreadsheetFile(file) {
  const name = String((file && file.name) || '').toLowerCase();
  const type = String((file && file.type) || '').toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return false;
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return true;
  return type.includes('spreadsheetml') || type.includes('ms-excel') || type.includes('excel') || type.includes('sheet');
}

// Flatten a SheetJS workbook to CSV text — every non-empty sheet, in workbook
// order, blank-line separated (the same shape parseExcelItemsBySheet feeds the
// import AI on the Add Items page). Returns '' for an empty workbook.
export function workbookToCsvText(workbook, xlsxLib) {
  const names = (workbook && Array.isArray(workbook.SheetNames)) ? workbook.SheetNames : [];
  const sections = [];
  names.forEach((name) => {
    const sheet = workbook.Sheets && workbook.Sheets[name];
    if (!sheet) return;
    let csv = '';
    try {
      csv = xlsxLib.utils.sheet_to_csv(sheet, { blankrows: false });
    } catch (_) {
      return; // one unreadable sheet must not sink the readable ones
    }
    if (String(csv || '').trim()) sections.push(csv);
  });
  return sections.join('\n\n');
}

// The friendly failures the drawer surfaces, named so the upload card can tell
// the user WHICH file failed (the old anonymous "that file" made retries look
// like nothing happened).
export function spreadsheetReaderUnavailableError(fileName) {
  const name = fileName ? `“${fileName}”` : 'that spreadsheet';
  return new Error(`I couldn’t load the spreadsheet reader for ${name} — check your connection and try again, or save it as CSV and upload that.`);
}

export function spreadsheetUnreadableError(fileName) {
  const name = fileName ? `“${fileName}”` : 'that spreadsheet';
  return new Error(`I couldn’t read ${name} — is it a valid Excel file? If it keeps failing, save it as CSV and upload that.`);
}

export function spreadsheetEmptyError(fileName) {
  const name = fileName ? `“${fileName}”` : 'That spreadsheet';
  return new Error(`${name} looks empty — check that the items are on a sheet with data and try again.`);
}

// "Attached “Order_Wine.xlsx” — reading…" / "Attached 3 files — reading…".
// Shown on the upload card the moment files are picked, so choosing a file
// (especially on a retry) visibly confirms what got attached.
export function describeAttachedFiles(files) {
  const list = Array.from(files || []);
  const names = list.map((f) => (f && f.name) || '').filter(Boolean);
  if (list.length === 1) return names[0] ? `“${names[0]}”` : 'your file';
  return `${list.length} files`;
}

// Name the files that failed, for the upload card's error note. Caps at three
// names so a big batch doesn't turn the note into a wall of filenames.
export function describeFailedFiles(failedNames) {
  const names = (failedNames || []).filter(Boolean).map(String);
  if (!names.length) return 'that file';
  if (names.length <= 3) return names.map((n) => `“${n}”`).join(', ');
  return `${names.length} files (including “${names[0]}”)`;
}
